import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, ShieldCheck, Building2, Settings } from "lucide-react";

const BLITZ_LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_info-vault-27/artifacts/677lf5x2_blitzentrylogo_image.jpg";

export function Brand({ light = false, name = "BlitzEntry", tagline = "Paperwork, simplified", to = "/", logoUrl = null }) {
  const [imgOk, setImgOk] = useState(true);
  return (
    <Link to={to} className="flex items-center gap-2.5" data-testid="brand-logo">
      {logoUrl && imgOk ? (
        <img src={logoUrl} alt="logo" onError={() => setImgOk(false)} className="h-9 w-auto max-w-[120px] object-contain" />
      ) : (
        <img src={BLITZ_LOGO} alt="BlitzEntry" className="h-9 w-9 rounded-lg object-contain" />
      )}
      <span className="leading-tight">
        <span className={`block text-[15px] font-bold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
          {name}
        </span>
        <span className={`block text-[11px] font-medium ${light ? "text-blue-100" : "text-slate-400"}`}>
          {tagline}
        </span>
      </span>
    </Link>
  );
}

export function SiteHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const authed = user && user !== false;
  const brandName = authed && user.clinic_name ? user.clinic_name : "BlitzEntry";
  const brandTag = authed && user.clinic_name ? "Nimbus Portal" : "Paperwork, simplified";
  const logoUrl = authed && user.clinic_id ? `${API}/clinic/logo/${user.clinic_id}` : null;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Brand name={brandName} tagline={brandTag} logoUrl={logoUrl} />
        <nav className="flex items-center gap-2">
          {authed ? (
            <>
              {user.role === "patient" && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/my-companies")} data-testid="nav-companies">
                  <Building2 className="mr-1.5 h-4 w-4" /> Blitzer Businesses
                </Button>
              )}
              {user.role === "clinic_admin" && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="nav-admin">
                    <ShieldCheck className="mr-1.5 h-4 w-4" /> Submissions
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/admin/settings")} data-testid="nav-settings">
                    <Settings className="mr-1.5 h-4 w-4" /> Settings
                  </Button>
                </>
              )}
              {user.role === "super_admin" && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/super")} data-testid="nav-super">
                  <Building2 className="mr-1.5 h-4 w-4" /> Manage Nimbus Accounts
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="nav-logout">
                <LogOut className="mr-1.5 h-4 w-4" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login">
                Sign in
              </Button>
              <Button size="sm" className="bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={() => navigate("/register")} data-testid="nav-register">
                Get started
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
